import * as React from 'react';
import { UseFormRegister, FieldErrors, UseFormWatch, UseFormSetValue } from 'react-hook-form';
import { X } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import type { StoreProfileFormData } from '@/lib/validators';
import { Category } from '@run2sale/shared';
import { cn } from '@/lib/utils';

interface StoreCategoriesFormProps {
  register: UseFormRegister<StoreProfileFormData>;
  errors: FieldErrors<StoreProfileFormData>;
  watch: UseFormWatch<StoreProfileFormData>;
  setValue: UseFormSetValue<StoreProfileFormData>;
}

const CATEGORY_LABELS: Record<Category, string> = {
  [Category.Fashion]: 'Fashion',
  [Category.Electronics]: 'Electronics',
  [Category.Sports]: 'Sports',
  [Category.Beauty]: 'Beauty',
  [Category.Food]: 'Food',
  [Category.Home]: 'Home',
  [Category.Accessories]: 'Accessories',
  [Category.Kids]: 'Kids',
  [Category.Automotive]: 'Automotive',
  [Category.Health]: 'Health',
};

export function StoreCategoriesForm({
  errors,
  watch,
  setValue,
}: StoreCategoriesFormProps) {
  const selectedCategories = watch('categories') || [];
  const brands = watch('brands') || [];
  const [brandInput, setBrandInput] = React.useState('');

  const toggleCategory = (category: string) => {
    const current = selectedCategories;
    if (current.includes(category)) {
      setValue(
        'categories',
        current.filter((c) => c !== category)
      );
    } else {
      setValue('categories', [...current, category]);
    }
  };

  const addBrand = () => {
    if (brandInput.trim() && !brands.includes(brandInput.trim())) {
      setValue('brands', [...brands, brandInput.trim()]);
      setBrandInput('');
    }
  };

  const removeBrand = (brand: string) => {
    setValue(
      'brands',
      brands.filter((b) => b !== brand)
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Categories & Brands</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <label className="mb-3 block text-sm font-medium text-gray-700">
            Store Categories *
          </label>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-5">
            {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => toggleCategory(value)}
                className={cn(
                  'rounded-lg border-2 px-4 py-2 text-sm font-medium transition-colors',
                  selectedCategories.includes(value)
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                )}
              >
                {label}
              </button>
            ))}
          </div>
          {errors.categories && (
            <p className="mt-1 text-sm text-destructive">{errors.categories.message}</p>
          )}
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700">Brands</label>
          <div className="flex gap-2">
            <Input
              placeholder="Add a brand (e.g., Nike, Zara)"
              value={brandInput}
              onChange={(e) => setBrandInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addBrand();
                }
              }}
            />
            <Button type="button" onClick={addBrand}>
              Add
            </Button>
          </div>
          {brands.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {brands.map((brand) => (
                <span
                  key={brand}
                  className="inline-flex items-center gap-1 rounded-full bg-secondary px-3 py-1 text-sm"
                >
                  {brand}
                  <button
                    type="button"
                    onClick={() => removeBrand(brand)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
